import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react'
import styled from 'styled-components'
import { useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import InfiniteScroll from 'react-infinite-scroller'
import CircularProgress from '@mui/material/CircularProgress'
import DayDivider from '@/v2/components/common/DayDivider'
import Typography from '@/v2/components/common/Typography'
import { useMessages } from '@/v2/hooks/messages/useMessages'
import RTMessages from '@/v2/hooks/subscriptions/messages/RTMessages'
import { getGroupedMessages, sortByCreation, getDate } from './utils'
import SendReply from './SendReply'
import Message from './Message'

const Container = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
`

const Wrapper = styled.div`
  width: 100%;
  flex: 1;
  overflow: auto;
  padding: 20px 16px 0 20px;
  display: flex;
  flex-direction: column;
  min-height: 0;

  @media (max-width: 1200px) {
    padding: 16px 12px 0 16px;
  }

  @media (max-width: 768px) {
    padding: 12px 12px 0 12px;
  }
`

const InputWrapper = styled.div`
  flex-shrink: 0;
  padding: 0 20px;

  @media (max-width: 1200px) {
    padding: 0 16px;
  }

  @media (max-width: 768px) {
    padding: 0;
  }
`

// PS: The Conversation endpoint returns the first batch of messages (first page),
//     the next pages we request from messages endpoint (messageHistory)
const MessagesHistory = ({
  historyMessages = [],
  currentPage: currentPageProp,
  totalPages,
}) => {
  const queryClient = useQueryClient()
  const [messages, setMessages] = useState([]) // second page or more
  const [currentPage, setCurrentPage] = useState(currentPageProp || 1)
  const { conversationId } = useParams()
  const { messageHistory } = useMessages(
    currentPage === 1 ? null : conversationId,
    currentPage
  )
  const debounceRef = useRef(null)
  const MAX_LOADED_MESSAGES = 500

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  useEffect(() => {
    scrollToBottom()
  }, [historyMessages])

  useEffect(() => {
    if (messageHistory) {
      const filteredMessages = messageHistory?.records.filter(
        record => !historyMessages.find(({ id }) => id === record.id)
      )
      setMessages(messages => {
        const updated = [...messages, ...filteredMessages]
        if (updated.length > MAX_LOADED_MESSAGES) {
          return updated.slice(updated.length - MAX_LOADED_MESSAGES)
        }
        return updated
      })
    }
  }, [messageHistory])

  const groupedMessages = useMemo(
    () => getGroupedMessages([...historyMessages, ...messages]),
    [historyMessages, messages]
  )

  const days = Object.keys(groupedMessages).sort((a, b) =>
    new Date(a) > new Date(b) ? 1 : -1
  )

  const hasMore = conversationId
    ? currentPage < (messageHistory?.totalPages || totalPages)
    : false

  const scrollRef = useRef()
  const scrollToBottom = () => {
    const element = scrollRef.current
    if (element) {
      element.scrollTo(0, element.scrollHeight)
    }
  }

  const handleLoadMore = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      if (hasMore) {
        if (historyMessages.length < 10) {
          queryClient.invalidateQueries({
            queryKey: ['Conversation', conversationId],
            cancelRefetch: true,
          })
        }
        setCurrentPage(currentPage + 1)
      }
    }, 1000)
  }, [
    hasMore,
    historyMessages.length,
    conversationId,
    currentPage,
    queryClient,
  ])

  return (
    <Container>
      <RTMessages />
      <Wrapper ref={scrollRef}>
        <InfiniteScroll
          isReverse
          element="div"
          useWindow={false}
          initialLoad={false}
          loadMore={handleLoadMore}
          hasMore={hasMore}
          loader={
            <CircularProgress
              key="loader"
              sx={{ margin: '10px auto', display: 'block' }}
            />
          }
          style={{ flex: 1 }}
        >
          {!hasMore && (
            <Typography textAlign="center" fontSize="14px">
              No more messages.
            </Typography>
          )}
          {days.map(day => (
            <div key={day}>
              <DayDivider date={getDate(day)} />
              {sortByCreation(groupedMessages[day]).map(message => (
                <Message key={message.id} message={message} />
              ))}
            </div>
          ))}
        </InfiniteScroll>
      </Wrapper>
      <InputWrapper>
        <SendReply onSend={scrollToBottom} />
      </InputWrapper>
    </Container>
  )
}

export default MessagesHistory
